import type { Role } from "@/lib/types";

export interface TrainingModule {
  id: string;
  title: string;
  summary: string;
  roles: Role[];
  steps: string[];
  tips?: string[];
  link?: { to: string; label: string };
}

const ADMIN: Role[] = ["organization_admin"];
const CONTENT: Role[] = ["organization_admin", "editor"];
const SUPER: Role[] = ["super_admin"];

export const TRAINING: TrainingModule[] = [
  {
    id: "overview", roles: CONTENT, title: "Premiers pas : le parcours complet",
    summary: "Les 5 étapes pour afficher votre premier contenu sur une télé.",
    steps: [
      "Médiathèque : ajoutez vos images, vidéos, messages texte ou pages Web.",
      "Listes de lecture : regroupez vos médias dans l'ordre voulu et fixez la durée de chacun.",
      "Groupes d'écrans (administrateur) : créez un groupe, par exemple « Salle à manger », et choisissez sa liste par défaut.",
      "Jumelage (administrateur) : saisissez le code à 6 chiffres affiché sur la télé pour l'ajouter à votre organisation.",
      "Planification : ajoutez des plages horaires pour changer de liste automatiquement, par exemple le menu du midi de 11 h 30 à 14 h.",
    ],
    tips: ["Toute modification est envoyée automatiquement aux télés : elles détectent la nouvelle version du contenu en quelques instants."],
  },
  {
    id: "pairing", roles: ADMIN, title: "Jumeler un téléviseur Android", link: { to: "/ecrans", label: "Ouvrir les écrans" },
    summary: "Ajouter une nouvelle télé à votre organisation en moins d'une minute.",
    steps: [
      "Allumez la télé et lancez l'application Innova-Sign : un code à 6 chiffres s'affiche.",
      "Dans le portail, cliquez sur « Jumeler un écran » en haut à droite.",
      "Saisissez le code, puis cliquez sur « Vérifier le code ».",
      "Donnez un nom clair à l'écran (ex. : Menu principal – comptoir), choisissez son groupe et son fuseau horaire.",
      "Cliquez sur « Approuver l'écran » : la télé télécharge son contenu et commence la lecture.",
    ],
    tips: ["Le code expire après 10 minutes et ne sert qu'une seule fois. S'il a expiré, relancez l'application sur la télé.", "Le nombre d'écrans est limité par votre forfait (voir le tableau de bord)."],
  },
  {
    id: "groups", roles: ADMIN, title: "Ajouter un téléviseur à un groupe d'écrans", link: { to: "/groupes", label: "Ouvrir les groupes" },
    summary: "Configurer plusieurs télés d'un coup avec le même contenu.",
    steps: [
      "Ouvrez « Groupes d'écrans », puis cliquez sur un groupe (ou créez-en un avec « Nouveau groupe »).",
      "Dans le panneau « Écrans du groupe », cliquez sur « Ajouter ».",
      "Cochez les télés à ajouter, puis confirmez : elles héritent aussitôt de la configuration du groupe.",
      "Pour retirer une télé, cliquez sur le X à côté de son nom.",
      "Autre méthode : sur la fiche d'un écran, choisissez le groupe et activez « Hériter de la configuration du groupe ».",
    ],
    tips: ["Un écran marqué « config. propre » est dans le groupe mais garde sa propre configuration."],
  },
  {
    id: "media", roles: CONTENT, title: "Gérer la médiathèque", link: { to: "/medias", label: "Ouvrir la médiathèque" },
    summary: "Téléverser des images et des vidéos, créer des messages et des pages Web.",
    steps: [
      "Cliquez sur « Téléverser » et glissez votre fichier : JPG, PNG, WEBP, GIF (25 Mo max) ou MP4, WEBM (300 Mo max).",
      "Donnez-lui un nom facile à retrouver, puis lancez l'envoi.",
      "Pour un message texte, une page Web, un flux RSS, la météo d'une ville ou une horloge, cliquez sur « Texte, Web, RSS, Météo, Horloge ».",
      "Supprimer un média le retire automatiquement de toutes les listes de lecture.",
    ],
    tips: ["Pour un affichage net, utilisez des images au format 16:9 (1920 × 1080)."],
  },
  {
    id: "playlists", roles: CONTENT, title: "Créer une liste de lecture", link: { to: "/listes", label: "Ouvrir les listes" },
    summary: "Ordonner vos médias et régler leur durée d'affichage.",
    steps: [
      "Cliquez sur « Nouvelle liste », nommez-la, puis « Créer et éditer ».",
      "Cliquez sur les médias à gauche pour les ajouter à la liste.",
      "Réglez la durée de chaque élément en secondes et changez l'ordre avec les flèches.",
      "Cliquez sur « Enregistrer » : les télés qui utilisent cette liste se mettent à jour.",
    ],
  },
  {
    id: "schedule", roles: ADMIN, title: "Planifier par plages horaires",
    summary: "Changer automatiquement de liste selon le jour et l'heure.",
    steps: [
      "Ouvrez un groupe ou un écran, puis la section « Planification horaire ».",
      "Choisissez d'abord la « Liste de lecture par défaut », jouée en dehors des plages.",
      "Cliquez sur « Ajouter une plage », choisissez la liste, les jours, l'heure de début et de fin.",
      "Si deux plages se chevauchent, celle qui a la priorité la plus haute l'emporte.",
      "Ajoutez si besoin un bandeau défilant (calligraphie, taille, couleurs, fond, vitesse, position, flux RSS), l'horloge (numérique ou analogique), une transition entre les médias (fondu, glissement, zoom), une couleur de fond et l'orientation, puis enregistrez.",
    ],
    tips: ["L'heure utilisée est celle du fuseau horaire de l'écran. L'onglet « Aperçu TV » de l'écran montre la liste jouée en ce moment.", "Section « Horaire d'allumage de la télé » : activez l'allumage / extinction automatique, puis indiquez les jours et les heures où la télé doit être allumée. En dehors de ces plages, elle s'éteint."],
  },
  {
    id: "monitoring", roles: ADMIN, title: "Suivre et dépanner les écrans", link: { to: "/tableau-de-bord", label: "Ouvrir le tableau de bord" },
    summary: "Savoir si vos télés fonctionnent et réagir en cas de problème.",
    steps: [
      "Le tableau de bord indique combien d'écrans sont en ligne ou hors ligne.",
      "Un écran passe « Hors ligne » s'il n'a pas contacté le serveur depuis 3 minutes : vérifiez l'alimentation et le Wi-Fi de la télé.",
      "L'onglet « Historique » d'un écran montre ses connexions et chaque nouveau contenu reçu.",
      "Si une télé est perdue ou volée, cliquez sur « Révoquer » : elle perd l'accès immédiatement.",
    ],
    tips: ["Contenu pas à jour ? Vérifiez que l'écran hérite bien du bon groupe et que la liste est enregistrée."],
  },
  {
    id: "users", roles: ADMIN, title: "Utilisateurs et rôles", link: { to: "/utilisateurs", label: "Ouvrir les utilisateurs" },
    summary: "Donner accès au portail à votre équipe.",
    steps: [
      "Cliquez sur « Ajouter », puis saisissez le nom et le courriel de la personne.",
      "Laissez le mot de passe vide pour qu'il soit généré : la personne reçoit un courriel de bienvenue avec ses identifiants.",
      "Rôle Administrateur : accès complet à l'organisation. Rôle Éditeur : médias et listes de lecture seulement.",
      "Désactivez un compte avec l'interrupteur « Actif » pour bloquer l'accès sans le supprimer.",
    ],
  },
  {
    id: "password", roles: ["super_admin", "organization_admin", "editor"], title: "Votre mot de passe",
    summary: "Changer ou récupérer votre mot de passe.",
    steps: [
      "Sur la page de connexion, cliquez sur « Mot de passe oublié ? ».",
      "Saisissez votre courriel : vous recevez un lien valide 1 heure.",
      "Choisissez un nouveau mot de passe d'au moins 8 caractères.",
    ],
  },
  {
    id: "orgs", roles: SUPER, title: "Gérer les organisations", link: { to: "/admin/organisations", label: "Ouvrir les organisations" },
    summary: "Créer des clients, fixer les forfaits et les quotas d'écrans.",
    steps: [
      "Cliquez sur « Nouvelle organisation », saisissez le nom, le forfait et le quota d'écrans.",
      "Sur la fiche de l'organisation, modifiez le statut d'abonnement, la date de renouvellement et les notes.",
      "Activez « Organisation suspendue » pour bloquer le portail et les télés d'un client.",
      "Ajoutez son premier administrateur avec « Ajouter » : il reçoit ses identifiants par courriel.",
    ],
  },
  {
    id: "org-users", roles: SUPER, title: "Comptes des clients",
    summary: "Promouvoir, désactiver ou supprimer des utilisateurs.",
    steps: [
      "Ouvrez la fiche d'une organisation : la liste des comptes est à droite.",
      "Changez le rôle avec le menu (Administrateur ou Éditeur).",
      "Désactivez temporairement avec l'interrupteur, ou supprimez définitivement avec la corbeille.",
      "Le bouton « Courriel test » de la page Organisations vérifie que l'envoi de courriels fonctionne.",
    ],
  },
];
